/**
 * FederationValidationsPage — V13.7
 *
 * Espace privé de la Fédération d'affiliation : voir les fiches d'athlètes de
 * SES sports affiliés qui sont en attente de validation, valider directement,
 * ou signaler une incohérence (l'athlète devra corriger + relancer).
 *
 * Auth requise : role "federation" (ou admin/copf pour supervision).
 * Route : /federation/validations
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { showError } from "@/lib/errors";
import { toast } from "sonner";
import { resolveAthletePhoto } from "@/lib/photoFallback";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ShieldCheck, ShieldAlert, AlertTriangle, ExternalLink, Loader2, RefreshCw,
  Search, Building2,
} from "lucide-react";

const TABS = [
  { v: "pending", label: "À valider" },
  { v: "inconsistency", label: "Incohérences signalées" },
  { v: "validated", label: "Validées" },
];

export default function FederationValidationsPage() {
  const { user } = useAuth();
  const canAccess = user && (user.roles || []).some((r) => ["federation", "copf", "admin"].includes(r));

  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [feds, setFeds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [action, setAction] = useState(null); // { kind, athlete }
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const fetchQueue = useCallback(async () => {
    if (!canAccess) return;
    setLoading(true);
    try {
      const { data } = await api.get("/federation-validations/queue", { params: { status: tab } });
      setItems(data.items || []);
      setFeds(data.federations || []);
    } catch (e) {
      showError(e, { prefix: "Validations fédération :" });
    } finally {
      setLoading(false);
    }
  }, [canAccess, tab]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const qq = q.toLowerCase();
    return items.filter((a) =>
      (a.full_name || "").toLowerCase().includes(qq) ||
      (a.slug || "").toLowerCase().includes(qq) ||
      (a.main_discipline || "").toLowerCase().includes(qq) ||
      (a.island || "").toLowerCase().includes(qq)
    );
  }, [items, q]);

  const submit = async () => {
    if (!action) return;
    setBusy(true);
    try {
      if (action.kind === "approve") {
        // V13.15 — Choix intelligent de la fédération à créditer :
        // on prend la première fédération de l'athlète qui existe aussi dans
        // la liste des fédés gérées par l'utilisateur (ou par le COPF/admin).
        const athFeds = action.athlete.federations || [];
        const matchedFed = feds.find((f) => athFeds.includes(f.name)) || feds[0];
        await api.post(`/athletes/${action.athlete.id}/validation/approve`, {
          federation_id: matchedFed?.id || null,
          federation_name: matchedFed?.name || null,
          as_role: "federation", // Explicite : validation au titre de la fédération
        });
        toast.success(`Palmarès validé au titre de ${matchedFed?.name || "la Fédération"}.`);
      } else if (action.kind === "signal") {
        if (!notes.trim()) { toast.error("Décris l'incohérence."); setBusy(false); return; }
        await api.post(`/athletes/${action.athlete.id}/validation/signal-inconsistency`, { notes: notes.trim() });
        toast.success("Incohérence signalée — l'athlète est notifié.");
      }
      setAction(null);
      setNotes("");
      await fetchQueue();
    } catch (e) {
      showError(e, { prefix: "Action :" });
    } finally { setBusy(false); }
  };

  if (!user) {
    return (
      <div className="mx-auto max-w-3xl p-8" data-testid="fed-val-need-login">
        <Card className="p-6 text-center">Connecte-toi pour accéder à cette page.</Card>
      </div>
    );
  }
  if (!canAccess) {
    return (
      <div className="mx-auto max-w-3xl p-8" data-testid="fed-val-forbidden">
        <Card className="p-6">
          <ShieldAlert className="h-6 w-6 text-[hsl(var(--aito-energy))]" />
          <h2 className="font-display text-xl font-bold mt-2">Accès fédération requis</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Cette page est réservée aux comptes de fédérations d'affiliation, au COPF et aux administrateurs.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-3 sm:px-6 lg:px-8 py-6 space-y-4" data-testid="federation-validations-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold flex items-center gap-2">
            <ShieldCheck className="h-7 w-7 text-emerald-600" />
            Validations — Ma fédération
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Les fiches d'athlètes affiliés à ta fédération, à valider ou à corriger.
          </p>
          {feds.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {feds.map((f) => (
                <Badge key={f.id} variant="outline" className="text-[10px]" data-testid={`fed-chip-${f.slug || f.id}`}>
                  <Building2 className="h-3 w-3 mr-1" /> {f.name}
                </Badge>
              ))}
            </div>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={fetchQueue} disabled={loading} data-testid="fed-val-refresh">
          {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Rafraîchir
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {TABS.map((t) => <TabsTrigger key={t.v} value={t.v} data-testid={`fed-val-tab-${t.v}`}>{t.label}</TabsTrigger>)}
        </TabsList>

        <div className="mt-3 relative max-w-md">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Rechercher un athlète…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="fed-val-search"
          />
        </div>

        {TABS.map((t) => (
          <TabsContent key={t.v} value={t.v} className="mt-4">
            {loading ? (
              <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
            ) : filtered.length === 0 ? (
              <Card className="p-8 text-center text-sm text-muted-foreground border-dashed">
                {feds.length === 0
                  ? "Aucune fédération n'est actuellement rattachée à ton compte. Contacte un administrateur."
                  : `Aucune fiche ${t.label.toLowerCase()}.`}
              </Card>
            ) : (
              <Card className="divide-y" data-testid="fed-val-list">
                {filtered.map((a) => (
                  <div key={a.id} className="flex items-center gap-3 p-3 hover:bg-muted/30 transition-colors" data-testid={`fed-val-row-${a.slug || a.id}`}>
                    <img
                      src={resolveAthletePhoto(a)}
                      alt=""
                      className="h-11 w-11 rounded-full object-cover object-top border shrink-0"
                      loading="lazy"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link to={`/athletes/${a.slug || a.id}`} target="_blank" rel="noreferrer" className="font-semibold hover:text-[hsl(var(--aito-lagoon))] truncate">
                          {a.full_name} <ExternalLink className="inline h-3 w-3" />
                        </Link>
                        {a.profile_validation_status === "pending" && (
                          <Badge variant="outline" className="text-[10px] border-amber-500 text-amber-700">En attente</Badge>
                        )}
                        {a.profile_validation_status === "inconsistency" && (
                          <Badge className="text-[10px] bg-orange-500 text-white">
                            <AlertTriangle className="h-3 w-3 mr-1" />Incohérence
                          </Badge>
                        )}
                        {a.profile_validation_status === "validated" && (
                          <Badge className="text-[10px] bg-emerald-600 text-white">
                            <ShieldCheck className="h-3 w-3 mr-1" />Validée
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {[a.main_discipline, a.island, (a.federations || []).join(" · ")].filter(Boolean).join(" — ")}
                      </div>
                      {a.profile_validation_requested_at && a.profile_validation_status === "pending" && (
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                          Demande du {new Date(a.profile_validation_requested_at).toLocaleDateString("fr-FR")}
                        </div>
                      )}
                      {a.profile_validation_notes && a.profile_validation_status === "inconsistency" && (
                        <p className="text-[11px] text-orange-700 mt-1 italic">« {a.profile_validation_notes} »</p>
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-1.5 shrink-0">
                      {a.profile_validation_status !== "validated" && (
                        <Button size="sm" onClick={() => setAction({ kind: "approve", athlete: a })} data-testid={`fed-val-approve-${a.id}`}>
                          <ShieldCheck className="h-3.5 w-3.5 mr-1" />Valider
                        </Button>
                      )}
                      {a.profile_validation_status !== "inconsistency" && (
                        <Button size="sm" variant="outline" onClick={() => setAction({ kind: "signal", athlete: a })} className="border-orange-400 text-orange-700 hover:bg-orange-50" data-testid={`fed-val-signal-${a.id}`}>
                          <AlertTriangle className="h-3.5 w-3.5 mr-1" />Signaler
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </Card>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {action && (
        <Dialog open={!!action} onOpenChange={(v) => { if (!v) { setAction(null); setNotes(""); } }}>
          <DialogContent data-testid="fed-val-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {action.kind === "approve"
                  ? <><ShieldCheck className="h-5 w-5 text-emerald-600" /> Valider la fiche</>
                  : <><AlertTriangle className="h-5 w-5 text-orange-600" /> Signaler une incohérence</>}
              </DialogTitle>
              <DialogDescription>{action.athlete.full_name}</DialogDescription>
            </DialogHeader>
            {action.kind === "signal" ? (
              <div>
                <label className="text-xs font-medium">Décris l'incohérence (visible par l'athlète)</label>
                <Textarea
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Ex : la médaille d'or 2024 n'apparaît pas dans nos résultats officiels ; merci de fournir un lien vers la source."
                  data-testid="fed-val-notes"
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                L'athlète verra apparaître le badge <strong>Fiche validée par {feds[0]?.name || "la Fédération"}</strong> sur sa fiche publique et son export PDF.
              </p>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setAction(null); setNotes(""); }} disabled={busy}>Annuler</Button>
              <Button onClick={submit} disabled={busy} variant={action.kind === "signal" ? "destructive" : "default"} data-testid="fed-val-confirm">
                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Confirmer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
